import React, { useEffect } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";

const AboutPageWrapper = styled.div`
  width: 100%;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #333 0%, #222 100%);
  color: white;
  padding: 80px 0;
  text-align: center;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 900;
  margin-bottom: 20px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 36px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 20px;
  margin-bottom: 0;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const ContentSection = styled.section`
  padding: 80px 0;

  @media (max-width: 768px) {
    padding: 60px 0;
  }

  &.bg-light {
    background: #f8f9fa;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: bold;
  color: #333;
  margin-bottom: 30px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 1024px) {
    gap: 40px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  &.reverse {
    @media (min-width: 769px) {
      direction: rtl;
      > * {
        direction: ltr;
      }
    }
  }
`;

const ContentText = styled.div`
  h3 {
    font-size: 28px;
    font-weight: bold;
    color: #333;
    margin-bottom: 20px;
    line-height: 1.3;

    @media (max-width: 768px) {
      font-size: 24px;
      margin-bottom: 15px;
    }
  }

  p {
    font-size: 16px;
    color: #666;
    line-height: 1.6;
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }

    @media (max-width: 768px) {
      font-size: 15px;
    }
  }
`;

const ContentImage = styled.div`
  width: 100%;
  height: 400px;
  border-radius: 12px;
  overflow: hidden;
  background: #f5f5f5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    height: 300px;
  }
`;

const StatsSection = styled.section`
  background: #ff6720;
  color: white;
  padding: 80px 0;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 40px;
  text-align: center;

  @media (max-width: 768px) {
    gap: 30px;
  }
`;

const StatItem = styled.div`
  h3 {
    font-size: 48px;
    font-weight: 900;
    margin-bottom: 10px;
    line-height: 1;

    @media (max-width: 768px) {
      font-size: 36px;
    }
  }

  p {
    font-size: 16px;
    margin: 0;
    opacity: 0.9;

    @media (max-width: 768px) {
      font-size: 14px;
    }
  }
`;

const ValuesSection = styled.section`
  padding: 80px 0;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-top: 50px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
    margin-top: 30px;
  }
`;

const ValueCard = styled.div`
  text-align: center;
  padding: 40px 20px;
  border-radius: 12px;
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const ValueIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #ff6720;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 32px;
  color: white;

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: 24px;
  }
`;

const ValueTitle = styled.h3`
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 10px;
  }
`;

const ValueDescription = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0;
`;

const TimelineSection = styled.section`
  background: #f8f9fa;
  padding: 80px 0;

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const Timeline = styled.div`
  max-width: 800px;
  margin: 50px auto 0;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #ff6720;
    transform: translateX(-50%);

    @media (max-width: 768px) {
      left: 20px;
      transform: none;
    }
  }

  @media (max-width: 768px) {
    margin-top: 30px;
  }
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 60px;
  position: relative;

  &:nth-child(even) {
    flex-direction: row-reverse;
    text-align: right;

    @media (max-width: 768px) {
      flex-direction: row;
      text-align: left;
    }
  }

  @media (max-width: 768px) {
    margin-bottom: 40px;
    padding-left: 60px;
  }
`;

const TimelineYear = styled.div`
  width: 100px;
  height: 100px;
  background: #ff6720;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  color: white;
  flex-shrink: 0;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    position: absolute;
    left: 0;
    width: 40px;
    height: 40px;
    font-size: 12px;
  }
`;

const TimelineContent = styled.div`
  flex: 1;
  padding: 0 40px;

  @media (max-width: 768px) {
    padding: 0;
  }

  h4 {
    font-size: 20px;
    font-weight: bold;
    color: #333;
    margin-bottom: 10px;

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }

  p {
    font-size: 14px;
    color: #666;
    line-height: 1.6;
    margin: 0;
  }
`;

const CTASection = styled.section`
  background: #333;
  color: white;
  padding: 80px 0;
  text-align: center;

  @media (max-width: 768px) {
    padding: 60px 0;
  }

  h2 {
    font-size: 36px;
    font-weight: bold;
    margin-bottom: 20px;

    @media (max-width: 768px) {
      font-size: 28px;
    }
  }

  p {
    font-size: 18px;
    margin-bottom: 30px;
    opacity: 0.9;

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: #ff6720;
  color: white;
  padding: 16px 32px;
  border-radius: 30px;
  font-weight: bold;
  font-size: 16px;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: #e55a1c;
    transform: translateY(-2px);
  }

  @media (max-width: 480px) {
    padding: 14px 28px;
    font-size: 15px;
  }
`;

const AboutPage = () => {
  useEffect(() => {
    document.title = "About Us | XPLOD-GROUPS";
    return () => {
      document.title = "Home | XPLOD-GROUPS";
    };
  }, []);

  return (
    <AboutPageWrapper>
      <HeroSection>
        <div className="container">
          <HeroTitle>About BLACK+DECKER</HeroTitle>
          <HeroSubtitle>
            For over 110 years, we've been making tools that help people build,
            create, and improve their world.
          </HeroSubtitle>
        </div>
      </HeroSection>

      <ContentSection>
        <div className="container">
          <ContentGrid>
            <ContentText>
              <h3>Our Story</h3>
              <p>
                BLACK+DECKER was founded in 1910 by S. Duncan Black and Alonzo
                G. Decker in Baltimore, Maryland. What started as a small
                machine shop has grown into one of the world's most trusted
                names in power tools and home improvement.
              </p>
              <p>
                From inventing the first portable electric drill with pistol
                grip and trigger switch in 1916 to pioneering cordless
                technology, we've consistently led the industry with innovative
                solutions that make work easier and more efficient.
              </p>
            </ContentText>
            <ContentImage>
              <img
                src="https://images.unsplash.com/photo-1504148455328-c376907d081c?w=600&h=400&fit=crop"
                alt="BLACK+DECKER workshop"
              />
            </ContentImage>
          </ContentGrid>
        </div>
      </ContentSection>

      <StatsSection>
        <div className="container">
          <StatsGrid>
            <StatItem>
              <h3>110+</h3>
              <p>Years of Innovation</p>
            </StatItem>
            <StatItem>
              <h3>100+</h3>
              <p>Countries Worldwide</p>
            </StatItem>
            <StatItem>
              <h3>1000+</h3>
              <p>Products in Our Lineup</p>
            </StatItem>
            <StatItem>
              <h3>50M+</h3>
              <p>Satisfied Customers</p>
            </StatItem>
          </StatsGrid>
        </div>
      </StatsSection>

      <ContentSection className="bg-light">
        <div className="container">
          <ContentGrid className="reverse">
            <ContentText>
              <h3>Innovation & Quality</h3>
              <p>
                Innovation is at the heart of everything we do. Our team of
                engineers and designers work tirelessly to create tools that are
                not only powerful and reliable, but also intuitive and easy to
                use.
              </p>
              <p>
                Every BLACK+DECKER product undergoes rigorous testing to ensure
                it meets our high standards for quality, durability, and
                performance. We're committed to providing tools that
                professionals and DIY enthusiasts can depend on, project after
                project.
              </p>
            </ContentText>
            <ContentImage>
              <img
                src="https://images.unsplash.com/photo-1581244277943-fe4a9c777189?w=600&h=400&fit=crop"
                alt="Product innovation"
              />
            </ContentImage>
          </ContentGrid>
        </div>
      </ContentSection>

      <ValuesSection>
        <div className="container">
          <SectionTitle>Our Values</SectionTitle>
          <ValuesGrid>
            <ValueCard>
              <ValueIcon>🔧</ValueIcon>
              <ValueTitle>Innovation</ValueTitle>
              <ValueDescription>
                We constantly push the boundaries of what's possible, creating
                tools that solve real problems and make work more efficient.
              </ValueDescription>
            </ValueCard>
            <ValueCard>
              <ValueIcon>⚡</ValueIcon>
              <ValueTitle>Quality</ValueTitle>
              <ValueDescription>
                Every product we make is built to last, with rigorous testing
                and premium materials ensuring long-lasting performance.
              </ValueDescription>
            </ValueCard>
            <ValueCard>
              <ValueIcon>🎯</ValueIcon>
              <ValueTitle>Accessibility</ValueTitle>
              <ValueDescription>
                We believe everyone should have access to quality tools, which
                is why we focus on creating products that are both powerful and
                affordable.
              </ValueDescription>
            </ValueCard>
          </ValuesGrid>
        </div>
      </ValuesSection>

      <TimelineSection>
        <div className="container">
          <SectionTitle>Our Journey</SectionTitle>
          <Timeline>
            <TimelineItem>
              <TimelineYear>1910</TimelineYear>
              <TimelineContent>
                <h4>Company Founded</h4>
                <p>
                  S. Duncan Black and Alonzo G. Decker establish BLACK+DECKER in
                  Baltimore, Maryland.
                </p>
              </TimelineContent>
            </TimelineItem>
            <TimelineItem>
              <TimelineYear>1916</TimelineYear>
              <TimelineContent>
                <h4>First Electric Drill</h4>
                <p>
                  Invention of the world's first portable electric drill with
                  pistol grip and trigger switch.
                </p>
              </TimelineContent>
            </TimelineItem>
            <TimelineItem>
              <TimelineYear>1961</TimelineYear>
              <TimelineContent>
                <h4>Cordless Revolution</h4>
                <p>
                  Launch of the first cordless drill, revolutionizing the power
                  tool industry.
                </p>
              </TimelineContent>
            </TimelineItem>
            <TimelineItem>
              <TimelineYear>2010</TimelineYear>
              <TimelineContent>
                <h4>Stanley Black & Decker</h4>
                <p>
                  Merger with Stanley Works creating Stanley Black & Decker, a
                  global leader in tools and storage.
                </p>
              </TimelineContent>
            </TimelineItem>
            <TimelineItem>
              <TimelineYear>2024</TimelineYear>
              <TimelineContent>
                <h4>Digital Innovation</h4>
                <p>
                  Continued leadership in smart tools and digital innovation for
                  the modern world.
                </p>
              </TimelineContent>
            </TimelineItem>
          </Timeline>
        </div>
      </TimelineSection>

      <CTASection>
        <div className="container">
          <h2>Ready to Power Your Projects?</h2>
          <p>
            Discover our complete range of innovative tools and accessories.
          </p>
          <CTAButton to="/collections/outdoor">Shop Our Products</CTAButton>
        </div>
      </CTASection>
    </AboutPageWrapper>
  );
};

export default AboutPage;
